<template>
  <transition name="VModal-fade">
    <div v-if="isOpen" class="VModal-overlay" @click="closeModal">
      <div
        :class="[
          'VModal',
          size,
          {
            'VModal-scroll': isScrollable,
            'VModal-open': isOpen,
            'VModal--card-mobile': cardOnMobile
          }
        ]"
        @click.stop
      >
        <div class="VModal-header">
          <h2 class="VModal-title">{{ title }}</h2>
          <button v-if="closeButton" class="VModal-close" @click="closeModal">
            <icon name="close-modal" />
          </button>
        </div>
        <div
          v-if="isOpen"
          class="VModal-body"
          :class="{ 'VModal-body-scroll': isScrollable }"
          ref="modalBody"
          @click.stop
        >
          <slot></slot>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
export default {
  name: 'Modal',
  props: {
    isOpen: {
      type: Boolean,
      required: false
    },
    title: {
      type: String,
      default: ''
    },
    size: {
      type: String,
      default: 'md'
    },
    closeButton: {
      type: Boolean,
      default: true
    },
    // Keep centered card with radius on mobile (skip fullscreen stretch).
    cardOnMobile: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    isScrollable() {
      // Check if the modal body element exists and if its height is greater than the window height
      return (
        this.isOpen &&
        this.$refs.modalBody &&
        this.$refs.modalBody.scrollHeight > window.innerHeight
      );
    }
  },
  watch: {
    isOpen: {
      immediate: true,
      handler(val) {
        if (val) {
          this.hideCrisp();
        } else {
          this.restoreCrisp();
        }
      }
    }
  },
  beforeDestroy() {
    this.restoreCrisp();
  },
  methods: {
    closeModal() {
      this.$emit('close');
    },
    hideCrisp() {
      try {
        if (window.$crisp && typeof window.$crisp.push === 'function') {
          window.$crisp.push(['do', 'chat:hide']);
        }
      } catch (e) {
        // Crisp unavailable — ignore.
      }
    },
    restoreCrisp() {
      try {
        if (
          window.$crisp &&
          typeof window.$crisp.push === 'function' &&
          window.innerWidth > 760
        ) {
          window.$crisp.push(['do', 'chat:show']);
        }
      } catch (e) {
        // Crisp unavailable — ignore.
      }
    }
  }
};
</script>

<style lang="scss">
.VModal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 9999;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 16px;
  box-sizing: border-box;
}

.VModal {
  background: #f7f8fa;
  box-shadow: 0 0 20px rgba(0, 0, 0, 0.2);
  border-radius: 16px;
  overflow: hidden;
  width: 100%;
  max-width: 90%;
  max-height: 90%;
  display: flex;
  flex-direction: column;
  opacity: 0;
  transition: all 0.3s ease-out;
}

.VModal-open {
  opacity: 1;
  overflow: auto;
}

.VModal-fade-enter-active,
.VModal-fade-leave-active {
  transition: all 0.3s ease-out;
}

.VModal-fade-enter,
.VModal-fade-leave-to {
  opacity: 0;
}

.VModal-scroll {
  overflow-y: auto;
}

.VModal-header {
  display: flex;
  align-items: center;

  padding: 8px 16px;
  background: linear-gradient(45deg, #409fff 0%, #0063c7 100%);
  border-top: 1px solid #e3e7ee;
  border-left: 1px solid #e3e7ee;
  border-right: 1px solid #e3e7ee;
  border-radius: 16px 16px 0px 0px;
}

.VModal-title {
  margin: 0;
  flex-grow: 1;
  font-weight: 600;
  font-size: 14px;
  line-height: 18px;
  color: #fff;
}

.VModal-close {
  border: none;
  background: none;
  cursor: pointer;
  stroke: rgba(255, 255, 255, 0.6);
  transition: 0.3s;
  &:hover {
    stroke: #fff;
  }
}

.VModal-body {
  padding: 16px;
  background-color: #fff;
}

.VModal-body-scroll {
  max-height: 70vh; /* adjust this value to your needs */
}

.sm {
  max-width: 400px;
}

.md {
  max-width: 600px;
}

.lg {
  max-width: 800px;
}
.full {
  max-width: calc(100% - 48px);
}

@media (max-width: 1023px) {
  .VModal {
    height: 100%;
    max-height: 100vh;
    min-height: calc(100 * var(--vh));
    border-radius: 0;
    max-width: 100%;
  }
  .VModal-header {
    border-radius: 0;
  }
  .full {
    max-width: 100%;
  }

  .VModal.VModal--card-mobile {
    height: auto;
    max-height: 90%;
    min-height: 0;
    border-radius: 16px;
    max-width: 100%;
    width: 100%;

    .VModal-header {
      border-radius: 16px 16px 0 0;
    }
  }
}
</style>
